/**
 * GP HTL Deals Tab
 * Shows list of GPHTL deals with filtering and pagination
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useGphtlDeals } from '../../../../../hooks/useGphtl';
import { GphtlDeal, GphtlDealsFilters } from '../../../../../types/gphtl.types';

// Format currency for display
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format date for display
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Get status chip color
const getInvoiceStatusColor = (status: string): 'success' | 'warning' | 'default' => {
  switch (status) {
    case 'created':
      return 'success';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

const getPaymentStatusColor = (status: string): 'success' | 'warning' | 'info' | 'default' => {
  switch (status) {
    case 'complete':
      return 'success';
    case 'partial':
      return 'warning';
    case 'pending':
      return 'info';
    default:
      return 'default';
  }
};

export const GphtlDealsTab: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<GphtlDealsFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error, refetch } = useGphtlDeals(
    { ...filters, search: searchQuery },
    page + 1,
    rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (field: keyof GphtlDealsFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setPage(0);
  };

  const openHubSpotDeal = (hubspotDealId: string) => {
    const portalId = process.env.REACT_APP_HUBSPOT_PORTAL_ID || '48214952';
    window.open(
      `https://app.hubspot.com/contacts/${portalId}/record/0-3/${hubspotDealId}`,
      '_blank'
    );
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load deals: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Search and Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          placeholder="Search deals..."
          value={searchQuery}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant={showFilters ? 'contained' : 'outlined'}
          startIcon={<FilterIcon />}
          onClick={() => setShowFilters(!showFilters)}
          size="small"
        >
          Filters
        </Button>
        <IconButton onClick={() => refetch()} size="small" title="Refresh">
          <RefreshIcon />
        </IconButton>
        {(Object.keys(filters).length > 0 || searchQuery) && (
          <Button variant="text" size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </Stack>

      {/* Extended Filters */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={filters.paymentStatus || ''}
                label="Payment Status"
                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="complete">Complete</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Invoice Status</InputLabel>
              <Select
                value={filters.invoiceStatus || ''}
                label="Invoice Status"
                onChange={(e) => handleFilterChange('invoiceStatus', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="none">None</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="From Date"
              type="date"
              size="small"
              value={filters.dateFrom || ''}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />

            <TextField
              label="To Date"
              type="date"
              size="small"
              value={filters.dateTo || ''}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />

            <TextField
              label="Deal Stage"
              size="small"
              value={filters.stage || ''}
              onChange={(e) => handleFilterChange('stage', e.target.value)}
              sx={{ minWidth: 150 }}
            />

            <TextField
              label="Owner Email"
              size="small"
              value={filters.owner || ''}
              onChange={(e) => handleFilterChange('owner', e.target.value)}
              sx={{ minWidth: 200 }}
            />
          </Stack>
        </Paper>
      )}

      {/* Deals Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Deal Name</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">GPHTL Share</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Invoice</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : !data?.data || data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No GP HTL deals found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((deal: GphtlDeal) => (
                <TableRow key={deal.deal_id} hover>
                  <TableCell>
                    <Tooltip title={`HubSpot Deal ID: ${deal.hubspot_deal_id}`}>
                      <span>{deal.deal_name}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{deal.customer_name}</TableCell>
                  <TableCell align="right">{formatCurrency(deal.deal_amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(deal.gphtl_share)}</TableCell>
                  <TableCell>
                    <Chip
                      label={deal.deal_stage || 'Unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deal.invoice_status}
                      size="small"
                      color={getInvoiceStatusColor(deal.invoice_status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={deal.payment_status}
                      size="small"
                      color={getPaymentStatusColor(deal.payment_status)}
                    />
                  </TableCell>
                  <TableCell>{formatDate(deal.created_at)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View in HubSpot">
                      <IconButton
                        size="small"
                        onClick={() => openHubSpotDeal(deal.hubspot_deal_id)}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={data?.totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default GphtlDealsTab;
